import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import TeamSpiritCalendar from './TeamSpiritCalendar'
import TeamSpiritHelp from './TeamSpiritHelp'
import TeamSpiritLoadError from './TeamSpiritLoadError'
import TeamSpiritMatchTable from './TeamSpiritMatchTable'
import TeamSpiritPreviousPhaseMatches from './TeamSpiritPreviousPhaseMatches'

const TeamSpiritView = ({ model }) => {
  const {
    loadError,
    cardStyle,
    summary,
    controls,
    possession,
    table,
    calendar,
  } = model
  const {
    t,
    selectedGroupTeamName,
    formatTournamentStatusLabel,
    selectedGroup,
    activeTournament,
    nextRoundSummary,
    nextMatchDetails,
  } = summary

  const {
    selectedGroupId,
    setSelectedGroupId,
    groups,
    selectedTournamentId,
    setSelectedTournamentId,
    tournaments,
    showUpdateDays,
    setShowUpdateDays,
    showPreviousPhase,
    setShowPreviousPhase,
    canSyncPreviousRound,
    simulateLatestRound,
    simulatePreviousPhase,
    forceLatestRoundSimulation,
    setForceLatestRoundSimulation,
    fetchLatestRoundSpirits,
    postMatchSync,
  } = controls

  const {
    leftInput: possessionLeftInput,
    setLeftInput: setPossessionLeftInput,
    leftPercent: possessionLeftPercent,
    rightPercent: possessionRightPercent,
    opponentName: possessionOpponentName,
    rightInput: possessionRightInput,
    setRightInput: setPossessionRightInput,
  } = possession

  const {
    worldCupCalendar,
    nationalCompetition,
    worldCompetition,
    currentCalendarPosition,
    dayLabels,
    shouldShowCalendarReset,
  } = calendar
  const previousPhaseMatches = table.previousPhaseMatches || []
  const getGroupSelectionId = group => {
    const phase = Number(group?.phase)
    return `${Number(group?.groupId) || 0}:${
      Number.isFinite(phase) && phase > 0 ? phase : 'unresolved'
    }`
  }
  const selectedGroupIndex = groups.findIndex(
    group => getGroupSelectionId(group) === String(selectedGroupId)
  )
  const groupCount = groups.length
  const currentGroupIndex = selectedGroupIndex >= 0 ? selectedGroupIndex : 0
  const previousGroup =
    groupCount > 1
      ? groups[(currentGroupIndex - 1 + groupCount) % groupCount]
      : null
  const nextGroup =
    groupCount > 1 ? groups[(currentGroupIndex + 1) % groupCount] : null
  const nationalLeagueSimulationUrl =
    Number.isFinite(Number(selectedTournamentId)) &&
    Number.isFinite(Number(selectedGroup?.season)) &&
    Number.isFinite(Number(selectedGroup?.groupId)) &&
    Number.isFinite(Number(selectedGroup?.phase))
      ? `/simulator/NT-${selectedTournamentId}?season=${selectedGroup.season}&groupId=${selectedGroup.groupId}&phase=${selectedGroup.phase}`
      : null

  return (
    <>
      <TeamSpiritLoadError message={loadError} cardStyle={cardStyle} />
      <Card className="mt-3 mb-4" style={cardStyle}>
        <Card.Body>
          <Row className="g-3 mb-3 align-items-stretch">
            <Col md={8} sm={12}>
              <TeamSpiritHelp t={t} />
                <div
                  className="mt-3"
                  style={{
                    border: '1px solid #d9dee7',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    padding: '10px 12px',
                    fontSize: '14px',
                    lineHeight: 1.45,
                  }}
                >
                  <div>
                    <strong>Joukkue:</strong> {selectedGroupTeamName}
                  </div>
                  <div>
                    <strong>Käynnissä:</strong>{' '}
                    {formatTournamentStatusLabel(activeTournament)}
                  </div>
                  <div>
                    <strong>Seuraava kierros:</strong> {nextRoundSummary}
                    {nextMatchDetails ? ` — ${nextMatchDetails}` : ''}
                  </div>
                </div>
              <Row className="g-3">
                <Col md={8} sm={12}>
                  <Form.Group>
                    <Form.Label>Kisat</Form.Label>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <Form.Select
                        value={selectedTournamentId || ''}
                        onChange={event =>
                          setSelectedTournamentId(
                            Number(event.target.value) || null
                          )
                        }
                      >
                        {tournaments.map(tournament => (
                          <option
                            key={tournament.tournamentId}
                            value={tournament.tournamentId}
                          >
                            {tournament.name || `Kilpailu ${tournament.tournamentId}`}
                          </option>
                        ))}
                      </Form.Select>
                      {nationalLeagueSimulationUrl ? (
                        <a
                          href={nationalLeagueSimulationUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-outline-primary flex-shrink-0 d-inline-flex align-items-center gap-1"
                          title="Avaa valitun maajoukkueturnauksen lohkon sarjasimulaatio"
                        >
                          Lohkon sarjasimulointi <span aria-hidden="true">↗</span>
                        </a>
                      ) : null}
                    </div>
                    <Form.Label>Lohko</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        aria-label="Edellinen lohko"
                        disabled={!previousGroup}
                        onClick={() =>
                          setSelectedGroupId(getGroupSelectionId(previousGroup))
                        }
                        size="sm"
                        variant="outline-secondary"
                      >
                        ‹
                      </Button>
                      <Form.Select
                        value={selectedGroupId || ''}
                        onChange={event =>
                          setSelectedGroupId(event.target.value || null)
                        }
                      >
                        {groups.map(group => (
                          <option
                            key={`${group.tournamentId}-${group.groupId}-${group.phase || 'unresolved'}`}
                            value={getGroupSelectionId(group)}
                          >
                            {group.groupName ||
                              group.groupLabel ||
                              `Lohko ${group.groupId}`}
                          </option>
                        ))}
                      </Form.Select>
                      <Button
                        aria-label="Seuraava lohko"
                        disabled={!nextGroup}
                        onClick={() =>
                          setSelectedGroupId(getGroupSelectionId(nextGroup))
                        }
                        size="sm"
                        variant="outline-secondary"
                      >
                        ›
                      </Button>
                    </div>
                    <div
                      className="mt-1"
                      style={{ fontSize: '11px', color: '#667085' }}
                    >
                      Oletuksena näytetään Suomen lohko. Valitse toinen lohko
                      ylhäältä.
                    </div>
                    <Form.Check
                      id="show-previous-phase"
                      type="checkbox"
                      className="mt-2"
                      label="Näytä edellinen vaihe"
                      checked={showPreviousPhase}
                      onChange={event =>
                        setShowPreviousPhase(event.target.checked)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4} sm={12}>
                  <div
                    className="d-flex flex-column justify-content-end h-100"
                    style={{ paddingBottom: '6px' }}
                  >
                    <Form.Check
                      id="show-update-days"
                      type="checkbox"
                      label={t('showIntermediateDays')}
                      checked={showUpdateDays}
                      onChange={event =>
                        setShowUpdateDays(event.target.checked)
                      }
                    />
                    {canSyncPreviousRound ? <>
                      <Button size="sm" variant="outline-secondary" className="mt-2 align-self-start" disabled={postMatchSync.loading} onClick={fetchLatestRoundSpirits}>
                        {postMatchSync.loading ? 'Haetaan...' : 'Hae viimeisimmän kierroksen henget'}
                      </Button>
                      <div className="d-flex align-items-center mt-2">
                        <Button size="sm" variant="outline-danger" disabled={postMatchSync.loading} onClick={simulateLatestRound}>
                          {postMatchSync.loading ? 'Simuloidaan...' : 'Simuloi'}
                        </Button>
                        <Form.Check
                          id="force-latest-round-simulation"
                          type="checkbox"
                          className="ms-2"
                          label="Yliaja"
                          checked={forceLatestRoundSimulation}
                          disabled={postMatchSync.loading}
                          onChange={event =>
                            setForceLatestRoundSimulation(event.target.checked)
                          }
                        />
                      </div>
                      {postMatchSync.message ? <div className="mt-1" style={{ fontSize: '11px', color: '#667085' }}>{postMatchSync.message}</div> : null}
                    </> : null}
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={4} sm={12} className="d-flex flex-column">
              <Card
                className="w-100 mt-auto"
                style={{
                  backgroundColor: '#fff4dc',
                  border: '1px solid #f0c36d',
                  borderRadius: '8px',
                }}
              >
                <Card.Body
                  className="d-flex flex-column"
                  style={{ padding: '8px 10px' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Badge
                      bg="warning"
                      text="dark"
                      style={{ fontSize: '11px' }}
                    >
                      Hallintalaskuri
                    </Badge>
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      lineHeight: 1.2,
                      color: '#5c4a1f',
                    }}
                  >
                    Klikkaa KK-arvoa tai anna arvo
                  </div>
                  <div className="d-flex align-items-end gap-2 mt-2">
                    <div
                      className="d-flex flex-column align-items-start"
                      style={{ minWidth: '88px' }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: 1.1,
                        }}
                      >
                        {selectedGroupTeamName}
                      </span>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={possessionLeftInput}
                        onChange={event =>
                          setPossessionLeftInput(event.target.value)
                        }
                        placeholder="0.00"
                        style={{
                          width: '88px',
                          minHeight: '24px',
                          fontSize: '12px',
                        }}
                      />
                    </div>

                    <div
                      style={{
                        minWidth: '130px',
                        height: '26px',
                        borderRadius: '6px',
                        border: '1px solid #c9d7ef',
                        backgroundImage:
                          possessionLeftPercent !== null &&
                          possessionRightPercent !== null
                            ? `linear-gradient(90deg, rgba(43, 110, 242, 0.26) 0%, rgba(43, 110, 242, 0.26) ${possessionLeftPercent.toFixed(
                                2
                              )}%, rgba(220, 53, 69, 0.26) ${possessionLeftPercent.toFixed(
                                2
                              )}%, rgba(220, 53, 69, 0.26) 100%)`
                            : 'linear-gradient(90deg, rgba(43, 110, 242, 0.12), rgba(220, 53, 69, 0.12))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#1f2328',
                        padding: '0 8px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {possessionLeftPercent !== null &&
                      possessionRightPercent !== null
                        ? `${possessionLeftPercent.toFixed(
                            2
                          )}% - ${possessionRightPercent.toFixed(2)}%`
                        : '---'}
                    </div>

                    <div
                      className="d-flex flex-column align-items-end"
                      style={{ minWidth: '88px' }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: 1.1,
                          minHeight: '14px',
                        }}
                      >
                        {possessionOpponentName || 'Vastustaja'}
                      </span>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={possessionRightInput}
                        onChange={event =>
                          setPossessionRightInput(event.target.value)
                        }
                        placeholder="0.00"
                        style={{
                          width: '88px',
                          minHeight: '24px',
                          fontSize: '12px',
                        }}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {showPreviousPhase ? (
            <TeamSpiritPreviousPhaseMatches
              matches={previousPhaseMatches}
              table={table}
              onSimulate={canSyncPreviousRound ? simulatePreviousPhase : null}
              simulationLoading={postMatchSync.loading}
            />
          ) : null}
          <TeamSpiritMatchTable table={table} />
        </Card.Body>
      </Card>

      <div className="mt-3 mb-4">
        {worldCupCalendar.map(season => (
          <TeamSpiritCalendar
            key={season.season}
            season={season}
            nationalCompetition={nationalCompetition}
            worldCompetition={worldCompetition}
            currentCalendarPosition={currentCalendarPosition}
            dayLabels={dayLabels}
            shouldShowCalendarReset={shouldShowCalendarReset}
          />
        ))}
      </div>
    </>
  )
}

export default TeamSpiritView
